import { useState } from 'react'
import { Modal, Button, Card, Row, Col, Badge } from 'react-bootstrap'
import { EMAIL_STARTERS, SMS_STARTERS, TemplateStarter } from '../../../constants/templateStarters'

type Props = {
    show: boolean
    onHide: () => void
    onSelect: (starter: TemplateStarter) => void
    onStartFromScratch: () => void
    defaultChannel?: 'EMAIL' | 'SMS'
}

const SMS_CHAR_LIMIT = 160

export const TemplateStarterModal = ({ show, onHide, onSelect, onStartFromScratch, defaultChannel }: Props) => {
    const [activeChannel, setActiveChannel] = useState<'EMAIL' | 'SMS'>(defaultChannel ?? 'EMAIL')
    const [previewStarter, setPreviewStarter] = useState<TemplateStarter | null>(null)

    const starters = activeChannel === 'EMAIL' ? EMAIL_STARTERS : SMS_STARTERS

    const handleSelect = (starter: TemplateStarter) => {
        setPreviewStarter(null)
        onSelect(starter)
    }

    return (
        <>
        <Modal show={show && !previewStarter} onHide={onHide} size='xl' centered>
            <Modal.Header closeButton>
                <Modal.Title>Choose a Template Starter</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className='text-muted mb-4'>
                    Start with a professional template or create your own from scratch
                </p>

                {/* Channel Tabs */}
                <div className='d-flex gap-2 mb-4 border-bottom pb-3'>
                    <button
                        type='button'
                        className={`btn btn-sm px-4 ${activeChannel === 'EMAIL' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveChannel('EMAIL')}
                    >
                        ✉️ Email Templates
                        <Badge bg='light' text='dark' className='ms-2'>{EMAIL_STARTERS.length}</Badge>
                    </button>
                    <button
                        type='button'
                        className={`btn btn-sm px-4 ${activeChannel === 'SMS' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveChannel('SMS')}
                    >
                        💬 SMS Templates
                        <Badge bg='light' text='dark' className='ms-2'>{SMS_STARTERS.length}</Badge>
                    </button>
                </div>

                {/* Email starters grid */}
                {activeChannel === 'EMAIL' && (
                    <Row className='g-3 mb-4'>
                        {EMAIL_STARTERS.map((starter) => (
                            <Col key={starter.id} md={4}>
                                <Card
                                    className='h-100'
                                    style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                                    onClick={() => handleSelect(starter)}
                                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.12)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '')}
                                >
                                    <Card.Body className='d-flex flex-column'>
                                        <div className='text-center mb-3'>
                                            <div style={{ fontSize: 44 }}>{starter.icon}</div>
                                        </div>
                                        <Card.Title className='text-center mb-1' style={{ fontSize: 15 }}>
                                            {starter.name}
                                        </Card.Title>
                                        <Card.Text className='text-muted text-center flex-grow-1' style={{ fontSize: 13 }}>
                                            {starter.description}
                                        </Card.Text>
                                        <div className='d-flex gap-2 mt-3'>
                                            <Button
                                                variant='outline-secondary'
                                                size='sm'
                                                className='flex-shrink-0'
                                                onClick={(e) => { e.stopPropagation(); setPreviewStarter(starter) }}
                                            >
                                                <i className='bi bi-eye'></i>
                                            </Button>
                                            <Button variant='primary' size='sm' className='flex-grow-1'>
                                                Use This Template
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* SMS starters list */}
                {activeChannel === 'SMS' && (
                    <div className='mb-4'>
                        <div className='alert alert-info py-2 small mb-3'>
                            <i className='bi bi-info-circle me-2'></i>
                            SMS messages are limited to <strong>{SMS_CHAR_LIMIT} characters</strong> per segment. Variables like <code>{'{{first_name}}'}</code> will expand when sent.
                        </div>
                        <Row className='g-3'>
                            {SMS_STARTERS.map((starter) => {
                                const charCount = starter.content.bodyText.length
                                const segments = Math.ceil(charCount / SMS_CHAR_LIMIT)
                                return (
                                    <Col key={starter.id} md={6}>
                                        <Card
                                            className='h-100'
                                            style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                                            onClick={() => handleSelect(starter)}
                                            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.12)')}
                                            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '')}
                                        >
                                            <Card.Body>
                                                <div className='d-flex align-items-start gap-3 mb-3'>
                                                    <span style={{ fontSize: 32 }}>{starter.icon}</span>
                                                    <div className='flex-grow-1'>
                                                        <div className='fw-semibold' style={{ fontSize: 14 }}>{starter.name}</div>
                                                        <div className='text-muted' style={{ fontSize: 12 }}>{starter.description}</div>
                                                    </div>
                                                    <Badge bg={segments > 1 ? 'warning' : 'success'} text={segments > 1 ? 'dark' : undefined}>
                                                        {charCount} chars · {segments} seg
                                                    </Badge>
                                                </div>
                                                {/* SMS preview bubble */}
                                                <div
                                                    className='rounded p-3 mb-3'
                                                    style={{ background: '#e8f4fd', fontSize: 13, lineHeight: 1.5, color: '#333', fontFamily: 'system-ui, sans-serif' }}
                                                >
                                                    {starter.content.bodyText}
                                                </div>
                                                <Button variant='success' size='sm' className='w-100'>
                                                    Use This Template
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                )
                            })}
                        </Row>
                    </div>
                )}

                <div className='text-center pt-3 border-top'>
                    <Button variant='outline-secondary' onClick={onStartFromScratch}>
                        <i className='bi bi-file-earmark-plus me-2'></i>
                        Start from Scratch
                    </Button>
                </div>
            </Modal.Body>
        </Modal>

        {/* Email HTML preview modal */}
        {previewStarter && (
            <Modal show onHide={() => setPreviewStarter(null)} size='lg' centered>
                <Modal.Header closeButton>
                    <Modal.Title className='fs-6'>
                        <span style={{ fontSize: 20 }} className='me-2'>{previewStarter.icon}</span>
                        {previewStarter.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className='p-0'>
                    <div
                        style={{ maxHeight: '65vh', overflowY: 'auto' }}
                        dangerouslySetInnerHTML={{ __html: previewStarter.content.bodyHtml ?? previewStarter.content.bodyText }}
                    />
                </Modal.Body>
                <Modal.Footer className='py-2 gap-2'>
                    <Button variant='outline-secondary' size='sm' onClick={() => setPreviewStarter(null)}>
                        Back
                    </Button>
                    <Button variant='primary' size='sm' onClick={() => handleSelect(previewStarter)}>
                        <i className='bi bi-check2 me-1'></i>Use This Template
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        </>
    )
}
